import React from "react";
import { Grid as MuiGrid, Container, Box } from "@mui/material";
import { useTheme } from "@mui/system";

// Main Grid Container
export const AutobiaContainer = ({
  children,
  maxWidth = "lg",
  disableGutters = false,
  sx = {},
}) => {
  const theme = useTheme();
  
  return (
    <Container
      maxWidth={maxWidth}
      disableGutters={disableGutters}
      sx={{
        px: disableGutters ? 0 : { xs: 2, sm: 3 },
        ...sx,
      }}
    >
      {children}
    </Container>
  );
};

// Grid System Component
export const AutobiaGrid = ({
  container = false,
  item = false,
  xs,
  sm,
  md,
  lg,
  xl,
  spacing = 2,
  direction = "row",
  justifyContent = "flex-start",
  alignItems = "stretch",
  children,
  sx = {},
  ...props
}) => {
  const theme = useTheme();

  return (
    <MuiGrid
      container={container}
      item={item}
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      spacing={spacing}
      direction={direction}
      justifyContent={justifyContent}
      alignItems={alignItems}
      sx={{
        ...(container && {
          margin: 0,
          width: "100%",
        }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiGrid>
  );
};

// Responsive Stack Component
export const AutobiaStack = ({
  direction = "column",
  spacing = 2,
  divider,
  alignItems = "stretch",
  justifyContent = "flex-start",
  children,
  responsive = false,
  sx = {},
}) => {
  const theme = useTheme();
  
  const responsiveDirection = responsive
    ? { xs: "column", sm: direction }
    : direction;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: responsiveDirection,
        alignItems: alignItems,
        justifyContent: justifyContent,
        gap: theme.spacing(spacing),
        ...sx,
      }}
    >
      {React.Children.map(children, (child, index) => (
        <React.Fragment key={index}>
          {index > 0 && divider && (
            <Box sx={{ 
              flexShrink: 0,
              ...(direction === "row" ? { height: "100%" } : { width: "100%" })
            }}>
              {divider}
            </Box>
          )}
          {child}
        </React.Fragment>
      ))}
    </Box>
  );
};

// Flex Box Utility
export const AutobiaFlex = ({
  direction = "row",
  wrap = "nowrap",
  justifyContent = "flex-start",
  alignItems = "center",
  alignContent = "stretch",
  gap = 0,
  children,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: direction,
        flexWrap: wrap,
        justifyContent: justifyContent,
        alignItems: alignItems,
        alignContent: alignContent,
        gap: theme.spacing(gap),
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Responsive Breakpoint Hook
export const useAutobiaBreakpoints = () => {
  const theme = useTheme();
  
  return {
    xs: theme.breakpoints.up("xs"),
    sm: theme.breakpoints.up("sm"),
    md: theme.breakpoints.up("md"),
    lg: theme.breakpoints.up("lg"),
    xl: theme.breakpoints.up("xl"),
    down: {
      xs: theme.breakpoints.down("xs"),
      sm: theme.breakpoints.down("sm"),
      md: theme.breakpoints.down("md"),
      lg: theme.breakpoints.down("lg"),
      xl: theme.breakpoints.down("xl"),
    },
    only: {
      xs: theme.breakpoints.only("xs"),
      sm: theme.breakpoints.only("sm"),
      md: theme.breakpoints.only("md"),
      lg: theme.breakpoints.only("lg"),
      xl: theme.breakpoints.only("xl"),
    },
  };
};

export default AutobiaGrid;